import { useCallback, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Card,
  Chip,
  CardContent,
  Divider,
  Grid,
  Stack,
  Button,
  TextField,
  Container,
  Typography,
  Breadcrumbs,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Link,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import InputMask from 'react-input-mask';
import accountService from '../services/account.service';
import { loading } from '../reducers/general.reducer';
import { daysOfWeek } from '../utils/dateTime';

const initialData = {
  id: '',
  name: '',
  address: '',
  hours: [
    {
      day: 1,
      init: '09:00',
      end: '19:00',
      open: false,
    },
    {
      day: 2,
      init: '09:00',
      end: '19:00',
      open: false,
    },
    {
      day: 3,
      init: '09:00',
      end: '19:00',
      open: false,
    },
    {
      day: 4,
      init: '09:00',
      end: '19:00',
      open: false,
    },
    {
      day: 5,
      init: '09:00',
      end: '19:00',
      open: false,
    },
    {
      day: 6,
      init: '09:00',
      end: '19:00',
      open: false,
    },
    {
      day: 0,
      init: '09:00',
      end: '19:00',
      open: false,
    },
  ],
  active: false,
};

export default function Page() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [data, setData] = useState(initialData);

  const fetchData = useCallback(async () => {
    dispatch(loading(true));
    const resUser = await accountService.get(id);
    setData({ ...initialData, ...resUser });
    dispatch(loading(false));
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [fetchData, id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: data.id,
      name: data.name,
      address: data.address,
      submit: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().max(255).required('O NOME é obrigatório'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        dispatch(loading(true));
        await accountService.edit(id, {
          id,
          name: values.name,
          address: values.address,
          hours: data.hours,
          active: data.active,
        });
        dispatch(loading(false));
        navigate(-1);
      } catch (error) {
        console.log(error);
        let message = 'Ops! Algo de errado aconteceu';
        if (error.response && error.response.data && error.response.data.message[0]) {
          message = error.response.data.message.join(', ');
        }
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: message });
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <>
      <Helmet>
        <title> Editar Conta | BMENU </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            CONTAS
          </Typography>
        </Stack>
        <Breadcrumbs aria-label="breadcrumb" mb={5}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            Dashboard
          </Link>
          <Link underline="hover" color="inherit" onClick={() => navigate('/accounts')} style={{ cursor: 'pointer' }}>
            Contas
          </Link>
          <Typography color="text.primary">Editar</Typography>
        </Breadcrumbs>
        <Card>
          <CardContent>
            <Box>
              <form noValidate onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      error={!!(formik.touched.id && formik.errors.id)}
                      fullWidth
                      helperText={formik.touched.id && formik.errors.id}
                      label="ID"
                      name="id"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.id}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      error={!!(formik.touched.name && formik.errors.name)}
                      fullWidth
                      helperText={formik.touched.name && formik.errors.name}
                      label="NOME"
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.name}
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      error={!!(formik.touched.address && formik.errors.address)}
                      fullWidth
                      helperText={formik.touched.address && formik.errors.naaddressme}
                      label="ENDEREÇO"
                      name="address"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.address}
                      autoComplete="off"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <br />
                    <Divider>
                      {' '}
                      <Chip label="HORÁRIO DE FUNCIONAMENTO" size="small" />{' '}
                    </Divider>
                    <br />
                    <Table>
                      <TableBody>
                        {data.hours.map((hour, index) => (
                          <TableRow hover key={index} tabIndex={-1}>
                            <TableCell>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={hour && hour.open}
                                      onChange={() => {
                                        const _hours = [...data.hours];
                                        _hours[index] = { ...data.hours[index], open: !hour.open };
                                        setData({ ...data, hours: _hours });
                                      }}
                                    />
                                  }
                                  label={daysOfWeek[hour.day].toUpperCase()}
                                />
                              </FormGroup>
                            </TableCell>
                            <TableCell>
                              <InputMask
                                mask="99:99"
                                value={hour.init}
                                onChange={(e) => {
                                  const _hours = [...data.hours];
                                  _hours[index] = { ...data.hours[index], init: e.target.value };
                                  setData({ ...data, hours: _hours });
                                }}
                              >
                                {() => (
                                  <TextField fullWidth label="ABRE ÀS" name={`hourInit${index}`} autoComplete="off" />
                                )}
                              </InputMask>
                            </TableCell>
                            <TableCell>
                              <InputMask
                                mask="99:99"
                                value={hour.end}
                                onChange={(e) => {
                                  const _hours = [...data.hours];
                                  _hours[index] = { ...data.hours[index], end: e.target.value };
                                  setData({ ...data, hours: _hours });
                                }}
                              >
                                {() => (
                                  <TextField fullWidth label="FECHA ÀS " name={`hourEnd${index}`} autoComplete="off" />
                                )}
                              </InputMask>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>

                  {formik.errors.submit && (
                    <Grid item xs={12} textAlign={'center'}>
                      <Typography color="error" variant="body2">
                        {formik.errors.submit}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} textAlign={'right'}>
                    <Switch
                      checked={data && data.active}
                      color="success"
                      onChange={() => {
                        setData({ ...data, active: !data.active });
                      }}
                      name="active"
                    />
                    <Button variant="contained" type="submit">
                      SALVAR
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </CardContent>
          <Divider />
        </Card>
      </Container>
    </>
  );
}
